import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import aa from "../AdminPortal/adminApp.module.css";
import { DesktopDialog, HubSheet } from "../AdminPortal/HubPwa";
import DaySessionPanel from "./DaySessionPanel";
import SessionCalendarCard from "./SessionCalendarCard";
import { sessionColor } from "./calendarUtils";

function dayTitle(day) {
  if (!day) return "";
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function dayCountLabel(count) {
  if (count === 1) return "1 занятие";
  if (count >= 2 && count <= 4) return `${count} занятия`;
  return `${count} занятий`;
}

export default function CalendarSessionSheet({
  open,
  day,
  sessions,
  groups,
  branches,
  doctors = [],
  sessionId,
  onSelectSession,
  onClose,
  onUpdated,
}) {
  const [heldDay, setHeldDay] = useState(day);

  useEffect(() => {
    if (day) setHeldDay(day);
  }, [day]);

  const activeDay = day || heldDay;
  if (!open && !activeDay) return null;

  return (
    <CalendarSessionSheetBody
      open={open && Boolean(activeDay)}
      day={activeDay}
      sessions={sessions}
      groups={groups}
      branches={branches}
      doctors={doctors}
      sessionId={sessionId}
      onSelectSession={onSelectSession}
      onClose={onClose}
      onUpdated={onUpdated}
    />
  );
}

function CalendarSessionSheetBody({
  open,
  day,
  sessions,
  groups,
  branches,
  doctors = [],
  sessionId,
  onSelectSession,
  onClose,
  onUpdated,
}) {
  const isMobile = useIsMobile(768);
  const dayList = useMemo(
    () =>
      [...sessions]
        .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
        .sort(
          (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime),
        ),
    [sessions, day],
  );

  const preview =
    dayList.find((sess) => sess.id === sessionId) ||
    (sessionId ? { id: sessionId } : null);

  const sheetBackRef = useRef(() => false);
  const [panelAction, setPanelAction] = useState(null);
  const [dockFooter, setDockFooter] = useState(null);

  useEffect(() => {
    if (!sessionId) setPanelAction(null);
  }, [sessionId]);

  useEffect(() => {
    if (!open) setDockFooter(null);
  }, [open]);

  const showDetail = Boolean(sessionId);

  const sheetHeadTitle =
    panelAction === "reschedule"
      ? "Перенос"
      : panelAction === "competition"
        ? "Соревнование"
        : panelAction === "details"
          ? "Данные"
          : panelAction === "cancel"
            ? "Отмена"
            : "Занятие";

  const handleSheetBack = useCallback(() => {
    if (sheetBackRef.current?.()) return;
    onSelectSession(null);
  }, [onSelectSession]);

  const handleDockChange = useCallback((node) => {
    setDockFooter(node);
  }, []);

  const detailBody = showDetail ? (
    <DaySessionPanel
      sessionId={sessionId}
      sessionPreview={
        preview?.start_datetime || preview?.title ? preview : null
      }
      groups={groups}
      branches={branches}
      doctors={doctors}
      onUpdated={onUpdated}
      variant={isMobile ? "sheet" : "dialog"}
      sheetBackRef={sheetBackRef}
      onPanelActionChange={setPanelAction}
      onDismiss={onClose}
      onDockChange={isMobile ? undefined : handleDockChange}
    />
  ) : dayList.length === 0 ? (
    <div className={aa.calSheetEmpty}>
      <p className={aa.calSheetEmptyTitle}>Нет занятий</p>
      <p className={aa.calSheetEmptyHint}>
        В этот день ничего не запланировано
      </p>
    </div>
  ) : (
    <ul
      className={
        isMobile ? aa.calSheetList : aa.calSessionDialogList
      }
    >
      {dayList.map((sess) => {
        const groupIdx = groups.findIndex((g) => g.id === sess.group?.id);
        const color = sessionColor(sess, groupIdx);
        return (
          <li key={sess.id}>
            <SessionCalendarCard
              sess={sess}
              color={color}
              variant="grid"
              onClick={() => onSelectSession(sess.id)}
            />
          </li>
        );
      })}
    </ul>
  );

  if (!isMobile) {
    return (
      <DesktopDialog
        open={open}
        onClose={onClose}
        onBack={showDetail ? handleSheetBack : undefined}
        title={showDetail ? sheetHeadTitle : dayTitle(day)}
        subtitle={
          showDetail
            ? null
            : dayCountLabel(dayList.length)
        }
        titleId="cal-session-sheet-title"
        dialogClassName={aa.calSessionDialog}
        bodyClassName={
          showDetail ? aa.calSessionDialogBody : aa.calSessionDialogListBody
        }
        footer={
          showDetail && dockFooter ? (
            <div className={aa.calSessionDialogFoot}>{dockFooter}</div>
          ) : null
        }
      >
        {detailBody}
      </DesktopDialog>
    );
  }

  const sheetHeader = (
    <header className={aa.calSheetHead}>
      {showDetail ? (
        <button
          type="button"
          className={aa.calSheetBack}
          onClick={handleSheetBack}
        >
          ← Назад
        </button>
      ) : (
        <span className={aa.calSheetHeadSpacer} aria-hidden />
      )}
      <div className={aa.calSheetHeadCenter}>
        {showDetail ? (
          <p className={aa.calSheetHeadKicker}>{sheetHeadTitle}</p>
        ) : (
          <>
            <p className={aa.calSheetHeadKicker}>{dayTitle(day)}</p>
            <p className={aa.calSheetHeadSub}>
              {dayCountLabel(dayList.length)}
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        className={aa.calSheetClose}
        onClick={onClose}
        aria-label="Закрыть"
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <path
            d="M6 6l12 12M18 6 6 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </header>
  );

  return (
    <HubSheet
      open={open}
      onClose={onClose}
      titleId="cal-session-sheet-title"
      header={sheetHeader}
      sheetClassName={showDetail ? aa.calSheetHubDetail : aa.calSheetHub}
      bodyClassName={aa.calSheetBody}
    >
      {detailBody}
    </HubSheet>
  );
}
